import React, { useState } from "react";
import { api } from "../api";

export const errText = (e) => {
  const d = e?.response?.data?.detail;
  if (!d) return "Une erreur est survenue, réessayez.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || "").join(" ");
  return d.msg || String(d);
};

const Field = ({ label, testid, ...props }) => (
  <label className="block">
    <span className="text-xs text-zinc-400 block mb-1.5">{label}</span>
    <input {...props} data-testid={testid}
           className="w-full px-3 py-2.5 rounded-lg text-sm bg-black/40 border border-white/10 focus:border-cyan-400/60 outline-none transition-colors" />
  </label>
);

const TABS = [
  ["password", "Mot de passe"],
  ["code", "Code par email"],
  ["signup", "Créer un compte"],
];

/** Connexion (MDP ou code email), inscription réservée aux acheteurs, mot de passe oublié. */
export const ClientAuth = ({ onLogged }) => {
  const [tab, setTab] = useState("password");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [challenge, setChallenge] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [info, setInfo] = useState("");

  const switchTab = (t) => { setTab(t); setChallenge(null); setCode(""); setErr(""); setInfo(""); };
  const run = async (fn) => {
    setBusy(true); setErr(""); setInfo("");
    try { await fn(); } catch (e) { setErr(errText(e)); } finally { setBusy(false); }
  };
  const done = (data) => onLogged(data.token, data.email);

  const submit = (e) => {
    e.preventDefault();
    run(async () => {
      if (tab === "password") return done((await api.post("/client/login", { email, password })).data);
      if (tab === "signup" && !challenge) {
        setChallenge((await api.post("/client/signup", { email, password })).data.challenge_token);
        return setInfo("Un code de vérification vient d'être envoyé à votre email.");
      }
      if (tab === "signup") return done((await api.post("/client/signup/verify", { challenge_token: challenge, code })).data);
      if ((tab === "code" || tab === "forgot") && !challenge) {
        const url = tab === "code" ? "/client/login/code" : "/client/password/forgot";
        const { data } = await api.post(url, { email });
        setChallenge(data.challenge_token);
        return setInfo(data.message);
      }
      if (tab === "code") return done((await api.post("/client/login/code/verify", { challenge_token: challenge, code })).data);
      return done((await api.post("/client/password/reset", { challenge_token: challenge, code, new_password: password })).data);
    });
  };

  const needsPassword = tab === "password" || (tab === "signup" && !challenge) || (tab === "forgot" && challenge);
  const label = {
    password: "Se connecter",
    code: challenge ? "Valider le code" : "Recevoir un code",
    signup: challenge ? "Valider mon compte" : "Créer mon compte",
    forgot: challenge ? "Changer le mot de passe" : "Recevoir un code",
  }[tab];

  return (
    <div className="max-w-md mx-auto glass rounded-2xl p-6 sm:p-8" data-testid="client-auth">
      <div className="flex gap-1 p-1 rounded-xl bg-black/30 mb-6">
        {TABS.map(([k, l]) => (
          <button key={k} type="button" onClick={() => switchTab(k)} data-testid={`client-auth-tab-${k}`}
                  className={`flex-1 py-2 rounded-lg text-xs font-semibold transition-colors ${tab === k ? "bg-cyan-500/20 text-cyan-200" : "text-zinc-400 hover:text-white"}`}>
            {l}
          </button>
        ))}
      </div>
      {tab === "signup" && !challenge && (
        <p className="text-xs text-zinc-400 mb-4">Réservé aux clients : utilisez l'email de votre commande Dr Trade Scalper Pro.</p>
      )}
      {tab === "forgot" && <p className="text-xs text-zinc-400 mb-4">Réinitialisation du mot de passe par code email.</p>}
      <form onSubmit={submit} className="space-y-4">
        <Field label="Email" type="email" required value={email} disabled={!!challenge}
               onChange={(e) => setEmail(e.target.value)} testid="client-auth-email" />
        {needsPassword && (
          <Field label={tab === "forgot" ? "Nouveau mot de passe (8 caractères min.)" : "Mot de passe"} type="password" required
                 minLength={tab === "password" ? undefined : 8} value={password}
                 onChange={(e) => setPassword(e.target.value)} testid="client-auth-password" />
        )}
        {challenge && (
          <Field label="Code reçu par email (6 chiffres)" inputMode="numeric" required value={code}
                 onChange={(e) => setCode(e.target.value)} testid="client-auth-code" />
        )}
        {info && <div className="text-xs text-emerald-300" data-testid="client-auth-info">{info}</div>}
        {err && <div className="text-xs text-red-400" data-testid="client-auth-error">{err}</div>}
        <button type="submit" disabled={busy} data-testid="client-auth-submit"
                className="btn-primary w-full py-2.5 rounded-lg text-sm font-semibold disabled:opacity-50">
          {busy ? "…" : label}
        </button>
      </form>
      <div className="flex justify-between mt-4 text-xs">
        {tab !== "forgot" ? (
          <button type="button" onClick={() => switchTab("forgot")} className="text-zinc-400 hover:text-white" data-testid="client-auth-forgot">
            Mot de passe oublié ?
          </button>
        ) : (
          <button type="button" onClick={() => switchTab("password")} className="text-zinc-400 hover:text-white">← Retour</button>
        )}
        {challenge && (
          <button type="button" onClick={() => { setChallenge(null); setCode(""); setInfo(""); }} className="text-zinc-400 hover:text-white"
                  data-testid="client-auth-restart">
            Changer d'email / renvoyer
          </button>
        )}
      </div>
    </div>
  );
};
